/**
 * Anmeldezustand.
 *
 * Der angemeldete Benutzer wird beim Start einmal geladen und danach im
 * Zwischenspeicher gehalten. Laeuft die Sitzung ab, meldet der Server 401;
 * die Oberflaeche zeigt dann wieder die Anmeldemaske, ohne die aktuelle
 * Seite zu verlieren.
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ModuleKey, Permission } from '@erp/shared';

import { ApiError, api } from './api/client';
import type { CurrentUser } from './api/types';

type AuthState = {
  user: CurrentUser | null;
  loading: boolean;
  /** Aktive Fachmodule der Installation. */
  modules: ModuleKey[];
  /** Verweise auf die Schwesteranwendungen (leer = nicht eingerichtet). */
  appLinks: AppLinks;
  /** Prueft, ob der angemeldete Benutzer eine Berechtigung besitzt. */
  can: (permission: Permission) => boolean;
  /** Prueft, ob ein Fachmodul in dieser Installation aktiv ist. */
  moduleEnabled: (module: ModuleKey) => boolean;
  login: (username: string, password: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

export type AppLinks = { portal: string | null; wissen: string | null };
const NO_LINKS: AppLinks = { portal: null, wissen: null };

type Session = { user: CurrentUser; modules: ModuleKey[]; appLinks: AppLinks };

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async (): Promise<Session | null> => {
      try {
        const result = await api.get<Session>('/api/auth/me');
        return { user: result.user, modules: result.modules ?? [], appLinks: result.appLinks ?? NO_LINKS };
      } catch (error) {
        // Nicht angemeldet ist kein Fehlerfall, sondern ein Zustand.
        if (error instanceof ApiError && error.isUnauthorized) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const user = data?.user ?? null;
  const modules = useMemo(() => data?.modules ?? [], [data]);
  const appLinks = data?.appLinks ?? NO_LINKS;

  const login = useCallback(
    async (username: string, password: string) => {
      const result = await api.post<Session>('/api/auth/login', {
        username,
        password,
      });
      queryClient.setQueryData<Session>(['auth', 'me'], {
        user: result.user,
        modules: result.modules ?? [],
        appLinks: result.appLinks ?? NO_LINKS,
      });
      return result.user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    queryClient.setQueryData(['auth', 'me'], null);
    // Alle zwischengespeicherten Geschaeftsdaten verwerfen - sie gehoeren
    // zum abgemeldeten Benutzer und duerfen dem naechsten nicht angezeigt
    // werden.
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [queryClient]);

  const can = useCallback(
    (permission: Permission) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const moduleEnabled = useCallback(
    (module: ModuleKey) => modules.includes(module),
    [modules],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading: isLoading, modules, appLinks, can, moduleEnabled, login, logout, refresh }),
    [user, isLoading, modules, can, moduleEnabled, login, logout, refresh, appLinks],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden.');
  return context;
}
